import React, { useState, useRef, useEffect } from 'react';
import './FileUploader.css';

/**
 * FileUploader - Componente minimalista para subir archivos
 * Detecta automáticamente: Excel (.xlsx, .xls), CSV (.csv), PDF (.pdf), Imágenes (.jpg, .png, .gif, .webp)
 */
const FileUploader = ({ onFileUpload, disabled = false, autoCleanup = false, clearTrigger = null }) => {
  const [uploadedFile, setUploadedFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [fileType, setFileType] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const fileInputRef = useRef(null);

  // Auto-cleanup cuando se solicita (después de enviar mensaje)
  useEffect(() => {
    if (autoCleanup && uploadedFile) {
      const timer = setTimeout(() => {
        handleClearFile();
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [autoCleanup]);

  // Limpiar cuando el componente padre lo solicita mediante clearTrigger
  useEffect(() => {
    if (clearTrigger !== null && uploadedFile) {
      handleClearFile();
    }
  }, [clearTrigger]);

  // Tipos de archivo soportados
  const SUPPORTED_FILES = {
    excel: {
      extensions: ['.xlsx', '.xls', '.xlsm'],
      mimeTypes: ['application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', 'application/vnd.ms-excel'],
      icon: '📊',
      label: 'Excel'
    },
    csv: {
      extensions: ['.csv'],
      mimeTypes: ['text/csv', 'application/csv', 'text/plain'],
      icon: '📊',
      label: 'CSV'
    },
    pdf: {
      extensions: ['.pdf'],
      mimeTypes: ['application/pdf'],
      icon: '📄',
      label: 'PDF'
    },
    image: {
      extensions: ['.jpg', '.jpeg', '.png', '.gif', '.webp'],
      mimeTypes: ['image/jpeg', 'image/png', 'image/gif', 'image/webp'],
      icon: '🖼️',
      label: 'Imagen'
    }
  };

  // Detectar tipo de archivo
  const detectFileType = (file) => {
    const fileName = file.name.toLowerCase();
    const mimeType = file.type.toLowerCase();

    for (const [type, config] of Object.entries(SUPPORTED_FILES)) {
      // Verificar extensión
      const hasValidExtension = config.extensions.some(ext => fileName.endsWith(ext));
      // Verificar MIME type
      const hasValidMimeType = config.mimeTypes.some(mime => mimeType.includes(mime));
      
      if (hasValidExtension || hasValidMimeType) {
        return type;
      }
    }

    return null;
  };

  // Manejar selección de archivo
  const handleFileSelect = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setIsProcessing(true);

    try {
      // Detectar tipo de archivo
      const type = detectFileType(file);
      
      if (!type) {
        alert(`Tipo de archivo no soportado. Formatos válidos:\n• Excel: ${SUPPORTED_FILES.excel.extensions.join(', ')}\n• CSV: ${SUPPORTED_FILES.csv.extensions.join(', ')}\n• PDF: ${SUPPORTED_FILES.pdf.extensions.join(', ')}\n• Imágenes: ${SUPPORTED_FILES.image.extensions.join(', ')}`);
        setIsProcessing(false);
        return;
      }

      setFileType(type);
      console.log(`[FileUploader] 📁 Tipo detectado: ${type} | Archivo: ${file.name}`);

      // Leer archivo como base64
      const reader = new FileReader();
      
      reader.onload = async (event) => {
        try {
          const base64String = event.target.result.split(',')[1];
          
          // Crear preview según tipo de archivo
          let previewData = null;
          if (type === 'image') {
            previewData = event.target.result; // URL completa para imágenes
          } else if (type === 'pdf') {
            previewData = {
              pages: '...',
              size: `${(file.size / 1024).toFixed(2)} KB`
            };
          } else if (type === 'excel' || type === 'csv') {
            previewData = {
              sheets: '...',
              size: `${(file.size / 1024).toFixed(2)} KB`
            };
          }

          setPreview(previewData);
          
          const fileData = {
            type,
            filename: file.name,
            mimeType: file.type,
            base64: base64String,
            size: file.size
          };

          setUploadedFile(fileData);
          
          // Notificar al componente padre
          if (onFileUpload) {
            onFileUpload(fileData);
          }

          console.log(`[FileUploader] ✅ Archivo listo: ${file.name}`);
          
        } catch (error) {
          console.error('[FileUploader] ❌ Error procesando archivo:', error);
          alert('Error al procesar el archivo. Por favor intenta de nuevo.');
        } finally {
          setIsProcessing(false);
        }
      };

      reader.onerror = () => {
        console.error('[FileUploader] ❌ Error leyendo archivo');
        alert('Error al leer el archivo');
        setIsProcessing(false);
      };

      reader.readAsDataURL(file);

    } catch (error) {
      console.error('[FileUploader] ❌ Error:', error);
      alert('Error al procesar el archivo');
      setIsProcessing(false);
    }
  };

  // Limpiar archivo subido
  const handleClearFile = () => {
    setUploadedFile(null);
    setPreview(null);
    setFileType(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
    if (onFileUpload) {
      onFileUpload(null);
    }
    console.log('[FileUploader] 🗑️ Archivo eliminado');
  };

  // Abrir selector de archivos
  const handleClick = () => {
    if (disabled || isProcessing) return;
    fileInputRef.current?.click();
  };

  return (
    <div className="file-uploader-container">
      <input
        ref={fileInputRef}
        type="file"
        accept=".xlsx,.xls,.xlsm,.csv,.pdf,.jpg,.jpeg,.png,.gif,.webp"
        onChange={handleFileSelect}
        style={{ display: 'none' }}
        disabled={disabled || isProcessing}
      />

      {!uploadedFile ? (
        <button
          className="upload-button"
          onClick={handleClick}
          disabled={disabled || isProcessing}
          title="Adjuntar archivo (Excel, CSV, PDF o Imagen)"
        >
          {isProcessing ? (
            <>
              <span className="spinner"></span>
              <span>Procesando...</span>
            </>
          ) : (
            <>
              <svg className="upload-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M21.44 11.05l-9.19 9.19a6 6 0 01-8.49-8.49l9.19-9.19a4 4 0 015.66 5.66l-9.2 9.19a2 2 0 01-2.83-2.83l8.49-8.48" />
              </svg>
              <span>Adjuntar</span>
            </>
          )}
        </button>
      ) : (
        <div 
          className="file-preview-card" 
          data-file-type={fileType}
        >
          <div className="file-preview-header">
            <div className="file-info">
              <span className="file-type-icon">{SUPPORTED_FILES[fileType].icon}</span>
              <div className="file-details">
                <span className="file-name" title={uploadedFile.filename}>
                  {uploadedFile.filename}
                </span>
                <span className="file-type-label">
                  {SUPPORTED_FILES[fileType].label} • {(uploadedFile.size / 1024).toFixed(1)} KB
                </span>
              </div>
            </div>
            <button
              className="clear-file-button"
              onClick={handleClearFile}
              title="Eliminar archivo"
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <line x1="18" y1="6" x2="6" y2="18" />
                <line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </button>
          </div>

          {/* Preview según tipo de archivo */}
          {preview && (
            <div className="file-preview-content">
              {fileType === 'image' && (
                <div className="image-preview">
                  <img src={preview} alt="Preview" />
                </div>
              )}
              
              {(fileType === 'pdf' || fileType === 'excel' || fileType === 'csv') && (
                <div className="document-preview">
                  <span className="document-icon">{SUPPORTED_FILES[fileType].icon}</span>
                  <div className="document-info">
                    <p>{fileType === 'pdf' ? 'Documento PDF' : fileType === 'csv' ? 'Archivo CSV' : 'Archivo Excel'}</p>
                    <p className="document-meta">{preview.size}</p>
                  </div>
                </div>
              )}
            </div>
          )}

          <div className="file-status">
            <span className="status-icon">✓</span>
            <span className="status-text">Listo para analizar</span>
          </div>
        </div>
      )}
    </div>
  );
};

export default FileUploader;